import React, { useEffect, useRef, useState } from 'react';
import { Sandbox } from '../sandbox';
import { DESIGN_SYSTEM } from '../../theme/designSystem';
import AudioControlPill from './AudioControlPill';
import './SplitScreenCourse.css';

// ── Shared z-index counter so last-touched card floats on top ──
let _dragZ = 10;

function computeCardDefaults() {
  if (typeof window === 'undefined') {
    return {
      chat:    { defaultLeft: 60,  defaultTop: 20, defaultW: 420, defaultH: 580 },
      sandbox: { defaultLeft: 500, defaultTop: 20, defaultW: 340, defaultH: 580 },
    };
  }
  const vw = Math.max(window.innerWidth, 320);
  const vh = Math.max(window.innerHeight - 42, 200); // subtract global nav
  const chatW  = Math.min(420, Math.round(vw * 0.31));
  const chatH  = Math.min(580, Math.round(vh * 0.88));
  const sbW    = Math.min(340, Math.round(vw * 0.26));
  const startL = Math.max(20, Math.round((vw - chatW - 20 - sbW) / 2));
  return {
    chat:    { defaultLeft: startL,             defaultTop: 20, defaultW: chatW, defaultH: chatH },
    sandbox: { defaultLeft: startL + chatW + 20, defaultTop: 20, defaultW: sbW,   defaultH: chatH },
  };
}

function DraggableCard({ defaultLeft, defaultTop, defaultW, defaultH, hidden = false, children }) {
  const posRef  = useRef({ x: defaultLeft, y: defaultTop });
  const sizeRef = useRef({ w: defaultW, h: defaultH });
  const [pos,  setPos]  = useState({ x: defaultLeft, y: defaultTop });
  const [size, setSize] = useState({ w: defaultW,    h: defaultH  });
  const [dragging, setDragging] = useState(false);
  const [z, setZ] = useState(10);

  function movePos(v)  { posRef.current  = v; setPos(v); }
  function moveSize(v) { sizeRef.current = v; setSize(v); }

  function onGripPointerDown(e) {
    if (e.button !== 0) return;
    e.preventDefault();
    const { x: px, y: py } = posRef.current;
    const mx = e.clientX, my = e.clientY;
    setDragging(true);
    setZ(++_dragZ);
    function onMove(ev) {
      movePos({ x: px + ev.clientX - mx, y: Math.max(0, py + ev.clientY - my) });
    }
    function onUp() {
      setDragging(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  function onResizePointerDown(e) {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const { w: sw, h: sh } = sizeRef.current;
    const mx = e.clientX, my = e.clientY;
    function onMove(ev) {
      moveSize({
        w: Math.max(260, sw + ev.clientX - mx),
        h: Math.max(200, sh + ev.clientY - my),
      });
    }
    function onUp() {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  return (
    <div
      className={`draggable-card${dragging ? ' is-dragging' : ''}`}
      style={{ left: pos.x, top: pos.y, width: size.w, height: size.h, zIndex: z, display: hidden ? 'none' : undefined }}
    >
      <div className="drag-grip" onPointerDown={onGripPointerDown} />
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>
      <div className="resize-handle" onPointerDown={onResizePointerDown} />
    </div>
  );
}

export function SplitScreenCourse({
  initialLevel = 1,
  chatAdapter,
  sandboxAdapter,
  onGraduate,
  onExit,
  initialPhase = null,
  initialPrefill = '',
  autoRun = false,
  autoStart = false,
  fromBot = false,
  returnPath = null,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(initialLevel);
  const [sandboxHistory, setSandboxHistory] = useState([]);
  const [botPrefill, setBotPrefill] = useState('');
  const [botPrefillAndRun, setBotPrefillAndRun] = useState(false);
  const hasSeenSeePhase = useRef((() => {
    try {
      return sessionStorage.getItem('om_sandbox_seen_see') === '1';
    } catch {
      return false;
    }
  })());

  function normalizePhase(requestedPhase) {
    const phase = String(requestedPhase || '').toLowerCase();
    if (!phase) return null;
    if ((phase === 'do' || phase === 'teach') && !hasSeenSeePhase.current) return 'see';
    return phase;
  }

  function markSeenPhase() {
    hasSeenSeePhase.current = true;
    try {
      sessionStorage.setItem('om_sandbox_seen_see', '1');
    } catch {
      // Non-fatal in restricted browser modes.
    }
  }

  const normalizedInitialPhase = normalizePhase(initialPhase);
  const [sandboxVisible, setSandboxVisible] = useState(Boolean(normalizedInitialPhase));
  const [activePhase, setActivePhase] = useState(normalizedInitialPhase || null);
  const chatEndRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (initialPrefill) {
      setBotPrefill(initialPrefill);
      if (autoRun) setBotPrefillAndRun(true);
    }
    if (normalizedInitialPhase) {
      setSandboxVisible(true);
      setActivePhase(normalizedInitialPhase);
      if (normalizedInitialPhase === 'see') markSeenPhase();
    }
    if (normalizedInitialPhase === 'teach') {
      setMessages([{
        id: cryptoRandomId(),
        role: 'assistant',
        content: "The sandbox is yours now. No template, no example \u2014 pick a real task from your own work and build the complete brief from scratch. When you're done, use the button above to return to your instructor.",
        timestamp: new Date().toISOString(),
      }]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!autoStart || !chatAdapter) return;
    let cancelled = false;
    setLoading(true);
    chatAdapter.send({ message: '', freshStart: true, currentLevel: initialLevel, sandboxHistory: [] })
      .then(data => {
        if (cancelled) return;
        if (data?.response) {
          setMessages([{
            id: cryptoRandomId(),
            role: 'assistant',
            content: String(data.response),
            timestamp: new Date().toISOString(),
          }]);
          if (audioRef.current?.isEnabled()) audioRef.current.play(String(data.response));
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && typeof onExit === 'function') {
        onExit();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onExit]);

  async function sendMessage() {
    const outgoing = input.trim();
    if (!outgoing || loading) return;

    const userMessage = {
      id: cryptoRandomId(),
      role: 'user',
      content: outgoing,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      if (!chatAdapter || typeof chatAdapter.send !== 'function') {
        throw new Error('chatAdapter.send is not configured');
      }
      const data = await chatAdapter.send({
        message: outgoing,
        currentLevel,
        sandboxHistory: sandboxHistory.slice(-10),
      });

      setMessages((prev) => [
        ...prev,
        {
          id: cryptoRandomId(),
          role: 'assistant',
          content: String(data?.response || 'No response.'),
          timestamp: new Date().toISOString(),
        },
      ]);

      if (data?.response && audioRef.current?.isEnabled()) {
        audioRef.current.play(String(data.response));
      }

      if (data?.prefillSandbox) {
        setBotPrefill(String(data.prefillSandbox));
        setBotPrefillAndRun(Boolean(data.autoRun));
      }

      if (data?.closeSplitScreen || data?.sandboxAction === 'close') {
        setSandboxVisible(false);
        setActivePhase(null);
        setBotPrefillAndRun(false);
      }

      if (data?.sandboxAction === 'open' && data?.sandboxPhase) {
        const nextPhase = normalizePhase(data.sandboxPhase);
        if (nextPhase === 'see') markSeenPhase();
        setSandboxVisible(true);
        setActivePhase(nextPhase || null);
      }

      if (data?.levelUp) {
        const newLevel = Number(data.newLevel || currentLevel);
        setCurrentLevel(newLevel);
        if (data?.graduated && typeof onGraduate === 'function') onGraduate(newLevel);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: cryptoRandomId(),
          role: 'assistant',
          content: 'Sorry, I hit an error. Please try again.',
          timestamp: new Date().toISOString(),
          isError: true,
        },
      ]);
    } finally {
      setLoading(false);
      setBotPrefillAndRun(false);
    }
  }

  const handleSandboxResponse = (runData) => {
    const entry = {
      id: cryptoRandomId(),
      prompt: runData.prompt,
      response: runData.response,
      model: runData.model,
      timestamp: new Date().toISOString(),
    };
    setSandboxHistory((prev) => [...prev, entry].slice(-50));
    if (chatAdapter && typeof chatAdapter.onSandboxEvent === 'function') {
      chatAdapter.onSandboxEvent(entry).catch(() => {});
    }
  };

  return (
    <div className={`split-screen-course ${sandboxVisible ? 'sandbox-open' : 'sandbox-closed'}`}>
      <div className="chat-panel-wrapper">
        <div className="chat-panel">
        <div className="chat-header">
          <div className="chat-header-left">
            <h2>Operator Method</h2>
            <div className="level-badge">Level {currentLevel}</div>
          </div>
          <AudioControlPill ref={audioRef} />
        </div>

        {fromBot && activePhase && PHASE_LABELS[activePhase] && (
          <div className="phase-banner">
            <span className="phase-label">{PHASE_LABELS[activePhase].label}</span>
            <span className="phase-desc">{PHASE_LABELS[activePhase].desc}</span>
          </div>
        )}

        <div className="chat-messages">
          {messages.map((msg) => (
            <div key={msg.id} className={`message ${msg.role} ${msg.isError ? 'error' : ''}`}>
              <div className="message-avatar">{msg.role === 'assistant' ? 'AI' : 'YOU'}</div>
              <div className="message-content">{msg.content}</div>
            </div>
          ))}

          {loading && (
            <div className="message assistant loading">
              <div className="message-avatar">AI</div>
              <div className="message-content">Thinking...</div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <div className="chat-input-area">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Type your response. Enter to send, Shift+Enter for newline."
            rows={2}
            disabled={loading}
          />
          <button onClick={sendMessage} disabled={loading || !input.trim()}>
            Send
          </button>
        </div>
      </div>
      </div>

      {sandboxVisible && (
        <div className="sandbox-panel-wrapper">
          <div className="sandbox-panel">
          <Sandbox
            runPrompt={sandboxAdapter && sandboxAdapter.run ? sandboxAdapter.run : null}
            onPromptChange={() => {}}
            onResponse={handleSandboxResponse}
            botPrefillPrompt={botPrefill}
            botPrefillAndRun={botPrefillAndRun}
            model="gpt-4o-mini"
          />
          </div>
        </div>
      )}
    </div>
  );
}

const PHASE_LABELS = {
  see: { label: 'SEE ONE', desc: 'Watch the instructor demonstrate the structure.' },
  do: { label: 'DO ONE', desc: 'Apply the structure to the assigned task.' },
  teach: { label: 'TEACH ONE', desc: 'Build your own brief from scratch. No template.' },
};

function cryptoRandomId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
